import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getTopicTags, postTopicTags } from '../../api/topics';

import { REQUEST } from '../../constants/request';

export const fetchTopicTags = createAsyncThunk(
  'topics/fetchTopicTags',
  async (formData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await getTopicTags(formData, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getState().topics;

      return !(loading.fetchTopicTags === REQUEST.pending);
    },
  },
);

export const sendTopicTags = createAsyncThunk(
  'topics/sendTopicTags',
  async (selectedTags, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });

    try {
      const response = await postTopicTags(selectedTags, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getState().topics;

      return !(loading.sendTopicTags === REQUEST.pending);
    },
  },
);

const initialState = {
  topics: {
    byId: {},
    allIds: [],
  },
  selectedTopics: [],
  requestId: {},
  loading: {},
  error: {},
  isVisibleTopicTags: false,
};

export const TopicsSlice = createSlice({
  name: 'topics',
  initialState,
  reducers: {
    setIsVisibleTopicTags: state => {
      state.isVisibleTopicTags = true;
    },
    setSelectedTopics: (state, { payload }) => {
      state.selectedTopics = payload;
    },
    addSelectedTag: (state, { payload }) => {
      state.selectedTopics.push(payload);
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchTopicTags.pending, (state, { meta }) => {
      state.loading.fetchTopicTags = REQUEST.pending;
      state.requestId.fetchTopicTags = meta.requestId;
    });
    builder.addCase(fetchTopicTags.fulfilled, (state, { meta, payload }) => {
      if (
        state.loading.fetchTopicTags === REQUEST.pending &&
        state.requestId.fetchTopicTags === meta.requestId
      ) {
        Object.entries(payload).forEach(([group, topics]) => {
          state.topics.byId[group] = [...topics];
          state.topics.allIds = [...state.topics.allIds, ...[group]];
        });
        state.loading.fetchTopicTags = REQUEST.idle;
        state.requestId.fetchTopicTags = undefined;
      }
    });
    builder.addCase(fetchTopicTags.rejected, (state, { error }) => {
      state.loading.fetchTopicTags = REQUEST.idle;
      state.requestId.fetchTopicTags = undefined;
      state.error.fetchTopicTags = error;
    });
    builder.addCase(sendTopicTags.pending, (state, { meta }) => {
      state.loading.sendTopicTags = REQUEST.pending;
      state.requestId.sendTopicTags = meta.requestId;
    });
    builder.addCase(sendTopicTags.fulfilled, (state, { meta }) => {
      if (
        state.loading.sendTopicTags === REQUEST.pending &&
        state.requestId.sendTopicTags === meta.requestId
      ) {
        state.isVisibleTopicTags = false;
        state.selectedTopics = [];
        state.loading.sendTopicTags = REQUEST.idle;
        state.requestId.sendTopicTags = undefined;
      }
    });
    builder.addCase(sendTopicTags.rejected, (state, { error }) => {
      state.loading.sendTopicTags = REQUEST.idle;
      state.requestId.sendTopicTags = undefined;
      state.error.sendTopicTags = error;
    });
  },
});

export const selectorGetTopicTags = state => state.topics.topics;
export const selectorGetSelectedTopicTags = state =>
  state.topics.selectedTopics;
export const selectorTopics = state => state.topics;

export const { setIsVisibleTopicTags, setSelectedTopics, addSelectedTag } =
  TopicsSlice.actions;

export default TopicsSlice.reducer;
